/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef } from 'react';
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { WEB_SIDEBAR_BREAKPOINT } from '../../../shared/layout/breakpoints';
import { AuthScreenFrame } from '../../../shell/brand/AuthScreenFrame';
import { OnboardingBrandRail } from './OnboardingBrandRail';
import { successRailSteps } from './onboardingRailCopy';
import { trackGA4Event } from '../../../shared/analytics/ga4';
import { hapticSuccess } from '../../../shared/ui/haptics';
import { VueltiFeliz } from '../../../shared/ui/VueltiFeliz';

type Props = {
  onGoSell: () => void;
  businessName?: string;
  /** Pediste Otros: aviso quieto en la pantalla, no toast encima del ✓. */
  showSupportFollowUp?: boolean;
  /** @deprecated use onGoSell */
  onDone?: () => void;
};

export function OnboardingSuccessScreen({
  onGoSell,
  businessName,
  showSupportFollowUp = false,
  onDone,
}: Props) {
  const goSell = onGoSell ?? onDone ?? (() => {});
  const { width } = useWindowDimensions();
  const useSplit = Platform.OS === 'web' && width >= WEB_SIDEBAR_BREAKPOINT;
  const name = businessName?.trim();
  const entrance = useRef(new Animated.Value(0)).current;

  // Conversión: el comerciante completó el registro/onboarding.
  // + payoff del alta: Vuelti feliz con entrada spring y haptic de éxito.
  useEffect(() => {
    trackGA4Event('sign_up', { method: 'onboarding' });
    hapticSuccess();
    Animated.spring(entrance, {
      toValue: 1,
      useNativeDriver: true,
      speed: 14,
      bounciness: 6,
    }).start();
  }, [entrance]);

  const form = (
    <View style={[styles.form, useSplit ? styles.formWide : styles.formMobile]}>
      <Animated.View
        style={{
          opacity: entrance,
          transform: [
            {
              scale: entrance.interpolate({
                inputRange: [0, 1],
                outputRange: [0.6, 1],
              }),
            },
          ],
        }}
      >
        <VueltiFeliz size={120} testID="onboarding.success-vuelti" />
      </Animated.View>
      <View style={styles.head}>
        <Text style={styles.title}>Listo. Ya podés vender.</Text>
        <Text style={styles.sub}>
          {name
            ? `${name} quedó armado. Cargá productos o hacé una venta de prueba.`
            : 'Tu local está armado. Empezá a cobrar o cargá productos.'}
        </Text>
        {showSupportFollowUp ? (
          <Text style={styles.supportHint} testID="onboarding.success-support-hint">
            Si hace falta sumar el rubro, te escribimos por soporte.
          </Text>
        ) : null}
      </View>
      <TouchableOpacity
        style={styles.primaryBtn}
        onPress={goSell}
        accessibilityRole="button"
        accessibilityLabel="Ir a vender"
        testID="onboarding.success-sell"
      >
        <Text style={styles.primaryLabel}>Ir a vender</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <AuthScreenFrame showWatermark={false} backgroundColor={useSplit ? '#FFFFFF' : colors.background}>
      {useSplit ? (
        <View style={styles.splitRoot}>
          <OnboardingBrandRail
            title={'Ya está\ntu local.'}
            subtitle="El siguiente paso: cargá un producto o servicio y empezá a vender."
            steps={successRailSteps()}
            footer="Todo tu negocio, en un solo lugar."
          />
          <View style={styles.splitFormCol}>
            <View style={styles.splitFormInner}>{form}</View>
          </View>
        </View>
      ) : (
        <View style={styles.mobileRoot}>{form}</View>
      )}
    </AuthScreenFrame>
  );
}

const styles = StyleSheet.create({
  splitRoot: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
  },
  splitFormCol: {
    flex: 1,
    minWidth: 360,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
  },
  splitFormInner: {
    paddingVertical: 36,
    paddingHorizontal: 32,
    width: '100%',
    maxWidth: 440,
    alignSelf: 'center',
  },
  mobileRoot: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: spacing.xl,
  },
  form: {
    width: '100%',
    gap: 14,
    alignItems: 'center',
  },
  formWide: {
    maxWidth: 400,
    alignSelf: 'center',
  },
  formMobile: {
    maxWidth: 400,
    alignSelf: 'center',
  },
  head: {
    gap: 8,
    marginBottom: 4,
    alignSelf: 'stretch',
  },
  title: {
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.4,
    fontWeight: '800',
    color: ticketColors.ink,
    textAlign: 'center',
  },
  sub: {
    fontSize: 14,
    lineHeight: 20,
    color: ticketColors.ink2,
    textAlign: 'center',
  },
  supportHint: {
    marginTop: 4,
    fontSize: 13,
    lineHeight: 18,
    color: ticketColors.ink2,
    opacity: 0.85,
    textAlign: 'center',
  },
  primaryBtn: {
    marginTop: 8,
    minHeight: 48,
    alignSelf: 'stretch',
    borderRadius: 18,
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryLabel: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
});
